import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { apiService } from '../services/apiService';

const capabilities = {
  add_rows: 'Add rows', reorder_rows: 'Reorder rows',
  create_columns: 'Create columns', delete_columns: 'Delete editable columns'
};
const blank = { name: '', read_columns: [], write_columns: [] };

export default function ShareBOM({ bom, onHide, onChange }) {
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [roleId, setRoleId] = useState('vendor');
  const [definition, setDefinition] = useState(blank);
  const [linkRole, setLinkRole] = useState('viewer');
  const [days, setDays] = useState(7);
  const [createdLink, setCreatedLink] = useState('');
  const [rowNumbers, setRowNumbers] = useState('');
  const [selectionChanged, setSelectionChanged] = useState(false);
  const restrictedLink = `${window.location.origin}/bom/${encodeURIComponent(bom.bom_id)}`;
  const load = useCallback(async () => {
    const result = await apiService.sharing(bom.bom_id, 'list');
    setSettings(result);
    return result;
  }, [bom.bom_id]);
  useEffect(() => {
    load().then(result => {
      const vendor = result.roles.vendor || { ...blank, name: 'Vendor' };
      setDefinition(vendor);
    })
      .catch(e => setError(e.response?.data?.error || e.message));
  }, [load]);

  const run = async (operation, data) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await apiService.sharing(bom.bom_id, operation, { ...data, revision: settings.revision });
      const refreshed = await load(); await onChange();
      setNotice('Sharing settings saved.');
      return { ...result, settings: refreshed };
    } catch (failure) {
      setError(failure.response?.data?.error || failure.message);
      if (failure.response?.status === 409) await load().catch(() => {});
      return null;
    } finally { setBusy(false); }
  };
  const copy = async link => {
    try { await navigator.clipboard.writeText(link); setNotice('Link copied. No invitation email was sent.'); }
    catch { setError('Clipboard unavailable. Select and copy the link below.'); }
  };
  const choices = settings ? [
    ['viewer', 'Viewer'], ['editor', 'Editor'], ['vendor', 'Vendor'],
    ...Object.entries(settings.roles).filter(([id]) => id !== 'vendor').map(([id, value]) => [id, value.name])
  ] : [];
  const options = choices.map(([id, name]) => <option key={id} value={id}>{name}</option>);
  const chooseRole = id => { setRoleId(id); setDefinition(id ? settings.roles[id] : { ...blank }); };
  const rowScope = definition.row_scope || { mode: 'all' };
  const rowPositions = new Map((bom.items || []).map(row => [row.line_item_id, row.order_index + 1]));
  const selectedNumbers = (rowScope.row_ids || []).map(id => rowPositions.get(id))
    .filter(number => number !== undefined).sort((a, b) => a - b).join(', ');
  const saveRole = async () => {
    let scope = rowScope;
    if (scope.mode === 'selected' && (selectionChanged || !scope.row_ids)) {
      const parts = rowNumbers.split(',').map(value => value.trim());
      if (!parts.length || parts.some(value => !/^[1-9]\d*$/.test(value))) {
        setError('Enter positive row numbers separated by commas, such as 1, 5, 9.');
        return;
      }
      scope = { mode: 'selected', row_numbers: parts.map(Number) };
    }
    const result = await run('save_role', { role_id: roleId || undefined, definition: { ...definition, row_scope: scope } });
    if (result) {
      setRoleId(result.role_id);
      setDefinition(result.settings.roles[result.role_id]);
      setSelectionChanged(false); setRowNumbers('');
    }
  };
  const toggle = (column, editable, enabled) => {
    const read = new Set(definition.read_columns);
    const write = new Set(definition.write_columns);
    if (editable) {
      if (enabled) { read.add(column); write.add(column); } else write.delete(column);
    } else if (enabled) read.add(column);
    else { read.delete(column); write.delete(column); }
    setDefinition({ ...definition, read_columns: [...read], write_columns: [...write] });
  };

  return <Modal show onHide={onHide} size="lg">
    <Modal.Header closeButton><Modal.Title>Share {bom.name}</Modal.Title></Modal.Header>
    <Modal.Body>
      {error && <Alert variant="danger">{error}</Alert>}
      {notice && <Alert variant="info">{notice}</Alert>}
      {!settings ? <p>Loading sharing settings…</p> : <fieldset disabled={busy}>
        <h5>Restricted access</h5>
        <p>Grant access by account email, then send the copied link yourself. Recipients must verify that email when creating an account.</p>
        <Form onSubmit={async event => {
          event.preventDefault();
          if (await run('grant', { user_email: email, role })) setEmail('');
        }}>
          <div className="d-flex gap-2 mb-2">
            <Form.Control type="email" required aria-label="Recipient email" placeholder="colleague@example.com" value={email} onChange={e => setEmail(e.target.value)} />
            <Form.Select aria-label="Recipient role" value={role} onChange={e => setRole(e.target.value)}>{options}</Form.Select>
            <Button type="submit">Grant access</Button>
          </div>
        </Form>
        <Form.Control aria-label="Restricted BOM link" readOnly value={restrictedLink} />
        <Button variant="link" onClick={() => copy(restrictedLink)}>Copy restricted link</Button>
        <Table size="sm" responsive>
          <thead><tr><th>Account email</th><th>Role</th><th>Access</th></tr></thead>
          <tbody>{settings.permissions.map(grant => <tr key={grant.user_id}>
            <td>{grant.user_email}</td>
            <td><Form.Select aria-label={`Role for ${grant.user_email}`} value={grant.role} onChange={e => run('update', { user_email: grant.user_email, role: e.target.value })}>
              {!choices.some(([id]) => id === grant.role) && <option value={grant.role}>Deleted role (no access)</option>}{options}
            </Form.Select></td>
            <td><Button size="sm" variant="outline-danger" onClick={() => run('revoke', { user_email: grant.user_email })}>Revoke</Button></td>
          </tr>)}</tbody>
        </Table>
        <hr /><h5>Anyone with the link — signed-in accounts only</h5>
        <p className="text-muted">Disabled by default. Generating a new link invalidates the previous link and its link-derived access. Explicit email grants remain independent.</p>
        {settings.link.enabled && <Alert variant="warning">Active until {new Date(settings.link.expires_at * 1000).toLocaleString()} ({settings.link.role}).</Alert>}
        <div className="d-flex gap-2 mb-2">
          <Form.Select aria-label="General link role" value={linkRole} onChange={e => setLinkRole(e.target.value)}>{options}</Form.Select>
          <Form.Select aria-label="Link expiration" value={days} onChange={e => setDays(Number(e.target.value))}>
            {[1, 7, 30].map(day => <option key={day} value={day}>{day} day{day !== 1 ? 's' : ''}</option>)}
          </Form.Select>
          <Button onClick={async () => {
            const result = await run('set_link', { enabled: true, role: linkRole, expires_days: days });
            if (result) setCreatedLink(`${restrictedLink}#share=${result.token}`);
          }}>Generate link</Button>
          <Button variant="outline-danger" disabled={!settings.link.enabled} onClick={async () => {
            if (await run('set_link', { enabled: false })) setCreatedLink('');
          }}>Disable link</Button>
        </div>
        {createdLink && <><Form.Control aria-label="Generated share link" readOnly value={createdLink} /><Button variant="link" onClick={() => copy(createdLink)}>Copy generated link</Button></>}
        <hr /><h5>Vendor and custom roles</h5>
        <p className="text-muted">For row-limited editing, configure Vendor or create a custom role and assign it above. Editor always has unrestricted editing.</p>
        <Form.Select className="mb-2" aria-label="Role to configure" value={roleId} onChange={e => {
          chooseRole(e.target.value); setSelectionChanged(false); setRowNumbers('');
        }}>
          <option value="vendor">Vendor</option>
          {Object.entries(settings.roles).filter(([id]) => id !== 'vendor').map(([id, value]) => <option key={id} value={id}>{value.name}</option>)}
          <option value="">Create custom role…</option>
        </Form.Select>
        <Form.Control className="mb-2" aria-label="Role name" placeholder="Role name" disabled={roleId === 'vendor'}
          value={definition.name} maxLength={80} onChange={e => setDefinition({ ...definition, name: e.target.value })} />
        <p className="text-muted">Editable fields must also be visible. Newly created columns are not automatically granted to these roles.</p>
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          <Table size="sm"><thead><tr><th>Column</th><th>Visible</th><th>Editable</th></tr></thead>
            <tbody>{bom.columns.map(column => <tr key={column.id}><td>{column.name}</td>
              <td><Form.Check aria-label={`Show ${column.name}`} checked={definition.read_columns.includes(column.id)} onChange={e => toggle(column.id, false, e.target.checked)} /></td>
              <td><Form.Check aria-label={`Edit ${column.name}`} checked={definition.write_columns.includes(column.id)} onChange={e => toggle(column.id, true, e.target.checked)} /></td>
            </tr>)}</tbody>
          </Table>
        </div>
        <Form.Group className="my-3" controlId="role-row-scope">
          <Form.Label>Editable rows</Form.Label>
          <Form.Select value={rowScope.mode} onChange={e => {
            setDefinition({ ...definition, row_scope: e.target.value === 'filter'
              ? { mode: 'filter', column_id: '', operator: 'equals', value: '' }
              : { mode: e.target.value } });
            setSelectionChanged(false); setRowNumbers('');
          }}>
            <option value="all">All rows</option>
            <option value="selected">Selected row numbers</option>
            <option value="filter">Rows matching a filter</option>
          </Form.Select>
        </Form.Group>
        {rowScope.mode === 'selected' && <Form.Group className="mb-3" controlId="role-row-numbers">
          <Form.Label>Row numbers</Form.Label>
          <Form.Control placeholder="1, 5, 9" value={selectionChanged ? rowNumbers : selectedNumbers} onChange={e => {
            setSelectionChanged(true); setRowNumbers(e.target.value);
          }} />
          <Form.Text>Use the # column, not the sorted/filtered display position. Permissions follow the same rows after reordering.</Form.Text>
        </Form.Group>}
        {rowScope.mode === 'filter' && <div className="d-flex gap-2 mb-3">
          <Form.Select aria-label="Role filter column" value={rowScope.column_id} onChange={e => setDefinition({
            ...definition, row_scope: { ...rowScope, column_id: e.target.value }
          })}>
            <option value="">Choose visible column</option>
            {bom.columns.filter(c => definition.read_columns.includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Form.Select>
          <Form.Select aria-label="Role filter operator" value={rowScope.operator} onChange={e => setDefinition({
            ...definition, row_scope: { ...rowScope, operator: e.target.value }
          })}><option value="equals">Equals</option><option value="contains">Contains</option></Form.Select>
          <Form.Control aria-label="Role filter value" placeholder="Filter value" maxLength={10000} value={rowScope.value} onChange={e => setDefinition({
            ...definition, row_scope: { ...rowScope, value: e.target.value }
          })} />
        </div>}
        {rowScope.mode !== 'all' && <Alert variant="info">
          Other rows remain visible but read-only. Add/reorder rows and create/delete columns are disabled for this role.
          Filters ignore case, update as data changes, and must still match after an edit.
        </Alert>}
        {roleId !== 'vendor' && rowScope.mode === 'all' && <div className="my-2">{Object.entries(capabilities).map(([id, label]) =>
          <Form.Check key={id} label={label} checked={!!definition[id]} onChange={e => setDefinition({ ...definition, [id]: e.target.checked })} />
        )}</div>}
        <Button onClick={saveRole}>Save role</Button>
        {roleId.startsWith('custom_') && <Button className="ms-2" variant="outline-danger" onClick={async () => {
          if (window.confirm('Delete this role? People assigned to it will lose access until reassigned.')) {
            if (await run('delete_role', { role_id: roleId })) { setRoleId('vendor'); setDefinition(settings.roles.vendor); setRole('viewer'); setLinkRole('viewer'); }
          }
        }}>Delete role</Button>}
      </fieldset>}
    </Modal.Body>
    <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
  </Modal>;
}
